import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Input, Row, Space, Statistic, Table, Tabs, message } from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import PageHeader from '../../components/business/PageHeader';
import RequestState from '../../components/business/RequestState';
import StatusTag from '../../components/business/StatusTag';
import { WorkbenchAction } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDateTime } from '../../utils/v2';

const actionRoute = (item: WorkbenchAction) => {
    const type = (item.sourceType || '').toUpperCase();
    if (type === 'OPPORTUNITY') return `/app/v2/opportunity/list?open=${item.sourceId}`;
    if (type === 'PROJECT') return `/app/v2/project/list?open=${item.sourceId}`;
    // 当前聚合契约未返回计划项/回款节点所属项目，不能误把来源 ID 当成项目 ID。
    if (type === 'PLAN_ITEM' || type === 'PAYMENT_NODE') return '/app/v2/project/list';
    if (type === 'COMPANY')
        return `/app/v2/customer/companies?open=${item.companyId || item.sourceId}`;
    if (type === 'PERSON') return `/app/v2/customer/persons?open=${item.sourceId}`;
    if (type === 'SIGNAL') return '/app/v2/development/signals';
    return '/app/v2/workbench/tasks';
};

const WorkbenchPage = () => {
    const history = useHistory();
    const [items, setItems] = useState<WorkbenchAction[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<V2ApiError | null>(null);
    const [activeFilter, setActiveFilter] = useState<string>('ALL');
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<any[]>([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await v2Api.workbench({ limit: 100 });
            setItems(result.items || []);
        } catch (e) {
            setError(asV2Error(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(
        () => ({
            ALL: items.length,
            TODAY: items.filter((item) => item.urgencyGroup === 'TODAY').length,
            OVERDUE: items.filter((item) => item.urgencyGroup === 'OVERDUE').length,
            CUSTOMER_WAITING: items.filter(
                (item) => item.urgencyGroup === 'CUSTOMER_WAITING' || item.customerWaiting
            ).length,
            NO_DATE: items.filter((item) => item.urgencyGroup === 'NO_DATE').length,
        }),
        [items]
    );

    const filtered =
        activeFilter === 'ALL'
            ? items
            : items.filter((item) => {
                  return (
                      item.urgencyGroup === activeFilter ||
                      (activeFilter === 'CUSTOMER_WAITING' && item.customerWaiting)
                  );
              });

    const search = async (keyword: string) => {
        if (!keyword.trim()) {
            setSearchResults([]);
            return;
        }
        setSearching(true);
        try {
            setSearchResults(await v2Api.search(keyword.trim(), 8));
        } catch (e) {
            message.error(asV2Error(e).message);
        } finally {
            setSearching(false);
        }
    };

    const cards = [
        ['ALL', '全部行动'],
        ['TODAY', '今日待处理'],
        ['OVERDUE', '已逾期'],
        ['CUSTOMER_WAITING', '客户等待'],
        ['NO_DATE', '缺少日期'],
    ];

    const columns = [
        {
            title: '紧急程度',
            dataIndex: 'urgencyGroup',
            width: 118,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '事项',
            dataIndex: 'sourceType',
            width: 120,
            render: (v: string) => <span className="v2-kicker">{v || 'ACTION'}</span>,
        },
        {
            title: '企业 / 业务对象',
            dataIndex: 'companyName',
            width: 180,
            render: (v: string, row: WorkbenchAction) =>
                v || row.relatedName || (row.companyId ? `企业 #${row.companyId}` : '—'),
        },
        {
            title: '需要处理',
            dataIndex: 'summary',
            render: (v: string, row: WorkbenchAction) => (
                <div>
                    <strong>{v || row.title || '待处理事项'}</strong>
                    {row.nextAction && <div className="v2-muted">下一步：{row.nextAction}</div>}
                </div>
            ),
        },
        {
            title: '截止时间',
            dataIndex: 'dueAt',
            width: 168,
            render: (v: string, row: WorkbenchAction) => formatDateTime(v || row.nextActionAt),
        },
        {
            title: '操作',
            key: 'action',
            width: 100,
            render: (_: any, row: WorkbenchAction) => (
                <Button type="link" onClick={() => history.push(actionRoute(row))}>
                    去处理
                </Button>
            ),
        },
    ];

    return (
        <div className="v2-page v2-workbench">
            <PageHeader
                eyebrow="SynRay CRM · 今日行动"
                title="早上好，先处理最重要的事"
                description="行动来自任务、客户回复、商机下一步、项目计划和财务节点；完成操作会更新来源对象。"
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={load}>
                            刷新
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => history.push('/app/v2/workbench/tasks')}
                        >
                            新建任务
                        </Button>
                    </Space>
                }
            />
            <Card className="v2-search-card" bordered={false}>
                <Input.Search
                    prefix={<SearchOutlined />}
                    loading={searching}
                    allowClear
                    enterButton="全局搜索"
                    placeholder="搜索企业、联系人、商机或项目"
                    onSearch={search}
                />
                {searchResults.length > 0 && (
                    <div className="v2-search-results">
                        {searchResults.map((item, index) => (
                            <Button
                                key={`${item.objectType}-${item.objectId}-${index}`}
                                type="link"
                                onClick={() =>
                                    history.push(
                                        actionRoute({
                                            sourceType: item.objectType,
                                            sourceId: item.objectId,
                                            companyId: item.companyId,
                                        } as any)
                                    )
                                }
                            >
                                {item.title} <span className="v2-muted">· {item.objectType}</span>
                            </Button>
                        ))}
                    </div>
                )}
            </Card>
            <Row gutter={[12, 12]} className="v2-stat-grid">
                {cards.map(([key, label]) => (
                    <Col key={key} xs={12} md={8} xl={key === 'ALL' ? 4 : 5}>
                        <Card
                            hoverable
                            className={
                                activeFilter === key ? 'v2-stat-card is-active' : 'v2-stat-card'
                            }
                            onClick={() => setActiveFilter(key)}
                        >
                            <Statistic
                                title={label}
                                value={(counts as any)[key]}
                                valueStyle={{
                                    color:
                                        key === 'OVERDUE' && counts.OVERDUE ? '#c13d2b' : undefined,
                                }}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>
            <Card
                className="v2-panel"
                title={
                    <Space>
                        统一行动列表 <Badge count={filtered.length} showZero />
                    </Space>
                }
            >
                <Tabs defaultActiveKey="mine">
                    <Tabs.TabPane tab="我的行动" key="mine" />
                    <Tabs.TabPane tab="管理队列" key="manage" disabled />
                    <Tabs.TabPane tab="团队视图 · 待启用" key="team" disabled />
                </Tabs>
                <RequestState
                    loading={loading}
                    error={error}
                    empty={!filtered.length}
                    onRetry={load}
                >
                    <Table
                        rowKey={(row: WorkbenchAction) =>
                            row.itemKey ||
                            `${row.sourceType}-${row.sourceId}-${row.actionCode || ''}`
                        }
                        columns={columns}
                        dataSource={filtered}
                        pagination={false}
                        scroll={{ x: 1050 }}
                    />
                </RequestState>
            </Card>
        </div>
    );
};

export default WorkbenchPage;
